import { getFieldTypeWidth, STANDARD_COLUMN_WIDTHS } from '../column-widths'
import { FieldTypeEnum } from '../custom-field.types'
import { ModuleColumnDefinition, ModuleTypeEnum } from '../module'
import { TimeEntry } from '../time-entry'

/**
 * Standard column definitions for the TimeEntry module
 * These correspond directly to the fields in the TimeEntry type
 *
 * @see TimeEntry type for the full data structure
 */
export const TIME_ENTRY_STANDARD_COLUMNS: ModuleColumnDefinition[] = [
  {
    id: 'actions',
    name: 'actions' as any, // Special action column
    label: '',
    description: 'Actions for this time entry',
    isVisible: true,
    isRequired: false,
    isSystem: true, // Cannot be disabled
    isFixed: true, // This column cannot be moved
    order: 0,
    fieldType: FieldTypeEnum.enum.STRING, // Will be overridden with custom renderer
    width: '50px',
    minWidth: '50px',
  },
  {
    id: 'moduleType',
    name: 'moduleType' as keyof TimeEntry,
    label: 'Linked To',
    description: 'Module this time entry is linked to (click to view details)',
    isVisible: true,
    isRequired: false,
    isSystem: true, // Cannot be disabled
    isFixed: true, // This column cannot be moved and is clickable for navigation
    order: 1,
    fieldType: FieldTypeEnum.enum.MODULE_SEARCH, // Use module search field
    width: '220px',
    minWidth: '180px',
  },
  {
    id: 'status',
    name: 'status' as keyof TimeEntry,
    label: 'Status',
    description:
      'Current status of the time entry (managed by approval workflow)',
    isVisible: true,
    isRequired: false,
    isSystem: true, // System-managed field
    isFixed: false,
    isReadOnly: true, // Status is controlled by approval workflow, not user-editable
    order: 2,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    width: '120px',
    minWidth: '100px',
  },
  {
    id: 'description',
    name: 'description' as keyof TimeEntry, // Type-safe key reference
    label: 'Description',
    description: 'Description of the time entry',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 3,
    fieldType: FieldTypeEnum.enum.STRING,
    width: '250px',
    minWidth: '200px',
  },
  {
    id: 'duration',
    name: 'duration' as keyof TimeEntry,
    label: 'Hours',
    description: 'Duration in hours (calculated or manually entered)',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    isReadOnly: true, // Edit via dialog to sync with start/end times
    order: 4,
    fieldType: FieldTypeEnum.enum.NUMBER,
    width: '100px',
    minWidth: '80px',
  },
  {
    id: 'entryDate',
    name: 'entryDate' as keyof TimeEntry,
    label: 'Date',
    description: 'The date this time entry is for',
    isVisible: true,
    isRequired: true,
    isSystem: false,
    isFixed: false,
    order: 5,
    fieldType: FieldTypeEnum.enum.DATE,
    width: '120px',
    minWidth: '100px',
  },
  {
    id: 'tags',
    name: 'tags' as keyof TimeEntry,
    label: 'Tags',
    description: 'Tags for organizing and categorizing the time entry',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 90, // Positioned near end of standard columns, custom fields come after at 91+
    fieldType: FieldTypeEnum.enum.STRING,
    width: '200px',
    minWidth: '150px',
  },
  {
    id: 'startTime',
    name: 'startTime' as keyof TimeEntry,
    label: 'Start Time',
    description: 'Start time of the time entry',
    isVisible: true,
    isRequired: false, // Now optional for manual entries
    isSystem: false,
    isFixed: false,
    isReadOnly: true, // Edit via dialog to sync with hours/end time
    order: 6,
    fieldType: FieldTypeEnum.enum.TIME,
    width: '100px',
    minWidth: '80px',
  },
  {
    id: 'endTime',
    name: 'endTime' as keyof TimeEntry,
    label: 'End Time',
    description: 'End time of the time entry',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    isReadOnly: true, // Edit via dialog to sync with hours/start time
    order: 7,
    fieldType: FieldTypeEnum.enum.TIME,
    width: '100px',
    minWidth: '80px',
  },
  {
    id: 'entryType',
    name: 'entryType' as keyof TimeEntry,
    label: 'Type',
    description: 'Entry type: Timer or Manual',
    isVisible: false, // Hide by default as it's less important
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 8,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    width: '80px',
    minWidth: '70px',
  },
  {
    id: 'billable',
    name: 'billable' as keyof TimeEntry,
    label: 'Billable',
    description: 'Whether this time entry is billable',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 9,
    fieldType: FieldTypeEnum.enum.BOOLEAN,
    width: '100px',
    minWidth: '80px',
  },
  {
    id: 'createdById',
    name: 'createdById' as keyof TimeEntry,
    label: 'Created By',
    description: 'User who created this time entry',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 9.5,
    fieldType: FieldTypeEnum.enum.USER,
    width: '160px',
    minWidth: '140px',
  },
  {
    id: 'createdAt',
    name: 'createdAt' as keyof TimeEntry,
    label: 'Created Date',
    description: 'Date when time entry was created',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 10,
    fieldType: FieldTypeEnum.enum.DATETIME,
    width: '180px',
    minWidth: '150px',
  },
]

/**
 * Helper function to get all available columns for time entry module,
 * combining standard and custom fields
 */
export function getTimeEntryColumns(
  customFields: any[] = [],
  visibilityPreferences?: Record<string, boolean>,
): ModuleColumnDefinition[] {
  // Start with standard columns
  const standardColumns = TIME_ENTRY_STANDARD_COLUMNS.map(column => ({
    ...column,
    // Override visibility if preferences exist
    isVisible:
      visibilityPreferences?.[column.id] !== undefined
        ? !!visibilityPreferences[column.id] // Ensure boolean type with !!
        : column.isVisible,
  }))

  // Default width for custom field columns based on field type
  const getCustomFieldWidth = (
    fieldType: string,
  ): { width: string; minWidth: string } => {
    switch (fieldType) {
      case FieldTypeEnum.enum.STRING:
        return { width: '180px', minWidth: '180px' }
      case FieldTypeEnum.enum.TEXT:
        return { width: '200px', minWidth: '200px' }
      case FieldTypeEnum.enum.NUMBER:
        return { width: '120px', minWidth: '120px' }
      case FieldTypeEnum.enum.BOOLEAN:
        return { width: '120px', minWidth: '120px' }
      case FieldTypeEnum.enum.DATE:
      case FieldTypeEnum.enum.DATETIME:
        return { width: '150px', minWidth: '150px' }
      case FieldTypeEnum.enum.TIME:
        return { width: '100px', minWidth: '80px' }
      case FieldTypeEnum.enum.DROPDOWN:
        return { width: '150px', minWidth: '150px' }
      case FieldTypeEnum.enum.MODULE_SEARCH:
        return { width: '220px', minWidth: '180px' }
      case FieldTypeEnum.enum.USER:
        return { width: '160px', minWidth: '140px' }
      default:
        return { width: '150px', minWidth: '150px' }
    }
  }

  // Map custom fields to column definitions
  const customColumns = customFields.map((field, index) => {
    const widthSettings = getCustomFieldWidth(field.fieldType)

    return {
      id: `custom_${field.fieldName}`,
      name: field.fieldName,
      label: field.label,
      description: `Custom field: ${field.label}`,
      isVisible:
        visibilityPreferences?.[`custom_${field.fieldName}`] !== undefined
          ? !!visibilityPreferences[`custom_${field.fieldName}`] // Ensure boolean type with !!
          : !!field.isActive,
      isRequired: false,
      isSystem: false,
      order: 91 + index, // Custom fields come after tags (order 90)
      fieldType: field.fieldType,
      width: widthSettings.width,
      minWidth: widthSettings.minWidth,
    }
  }) as ModuleColumnDefinition[]

  // Combine both types of columns
  return [...standardColumns, ...customColumns]
}

/**
 * Get module type for column definitions
 */
export function getTimeEntryModuleType(): ModuleTypeEnum {
  return ModuleTypeEnum.TIME_ENTRY
}
